/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { type Registry as Services, service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { registerDestructor } from '@ember/destroyable';
import type Owner from '@ember/owner';
import {
  TEAM_INBOX_GATE,
  TEAM_INBOX_PERMISSION,
} from 'uplisting-frontend/helpers/can-see-team-inbox';
import type { ModelRegistry } from 'ember-data/types/registries/model';

interface SidebarSignature {
  Element: HTMLElement;
}

const PINNED_CLASS = 'sidebar-pinned';

export default class UiSidebarComponent extends Component<SidebarSignature> {
  @service session!: Services['session'];
  @service storage!: Services['storage'];
  @service crisp!: Services['crisp'];
  @service features!: Services['features'];
  @service featureFlags!: Services['feature-flags'];

  @tracked settingsMenuOpen = false;
  @tracked moreMenuOpen = false;

  constructor(owner: Owner, args: object) {
    super(owner, args);

    window.document.addEventListener('click', this.handleDocumentClick);
    this.#applyPinnedClass();

    registerDestructor(this, () => {
      window.document.removeEventListener('click', this.handleDocumentClick);
      window.document.body.classList.remove(PINNED_CLASS);
    });
  }

  get pinned(): boolean {
    return this.storage.sidebarPinned;
  }

  @cached
  get currentUser(): ModelRegistry['user'] {
    return this.session.currentUser as ModelRegistry['user'];
  }

  @cached
  get protectionVisible(): boolean {
    return (
      this.features.isProtectionGateEnabled &&
      (this.currentUser.hasAccessTo('dashboard.index') ||
        this.currentUser.hasAccessTo('advanced_report_configurations.index') ||
        this.currentUser.hasAccessTo('disputes.index'))
    );
  }

  get complianceVisible(): boolean {
    return this.features.canSeeCompliance;
  }

  @cached
  get clientsNavLink(): { route: string; labelKey: string } | null {
    const { currentUser } = this.session;

    if (currentUser?.hasAccessTo('clients.index')) {
      return { route: 'clients', labelKey: 'sidebar.clients' };
    }

    if (currentUser?.hasAccessTo('client_statements.index')) {
      return { route: 'clients.statements', labelKey: 'sidebar.statements' };
    }

    return null;
  }

  @cached
  get teamUnreadVisible(): boolean {
    return (
      this.featureFlags.gate.enabled(TEAM_INBOX_GATE) &&
      (this.currentUser?.hasAccessTo(TEAM_INBOX_PERMISSION) ?? false)
    );
  }

  @cached
  get guestUnreadVisible(): boolean {
    return this.currentUser?.hasAccessTo('calendar.inbox') ?? false;
  }

  @cached
  get inboxVisible(): boolean {
    return this.guestUnreadVisible || this.teamUnreadVisible;
  }

  @cached
  get totalUnread(): number {
    const user = this.currentUser;
    const guests = this.guestUnreadVisible ? (user?.unreadMessages ?? 0) : 0;
    const team = this.teamUnreadVisible ? (user?.unreadTeamMessages ?? 0) : 0;

    return guests + team;
  }

  @cached
  get showUnreadMessagesCount(): boolean {
    return this.totalUnread > 0;
  }

  @cached
  get unreadMessages(): number | string {
    const { totalUnread } = this;

    if (totalUnread >= 500) {
      return '500+';
    }

    return totalUnread;
  }

  @cached
  get pendingAlerts(): number | string {
    const { pendingAlerts } = this.currentUser;

    if (pendingAlerts >= 500) {
      return '500+';
    }

    return pendingAlerts;
  }

  get userInitials(): string | undefined {
    return this.currentUser?.initials;
  }

  @action
  togglePinned(): void {
    this.storage.sidebarPinned = !this.pinned;
    this.#applyPinnedClass();
  }

  @action
  toggleSettingsMenu(event: MouseEvent): void {
    event.stopPropagation();
    this.settingsMenuOpen = !this.settingsMenuOpen;
  }

  @action
  toggleMoreMenu(event: MouseEvent): void {
    event.stopPropagation();
    this.moreMenuOpen = !this.moreMenuOpen;
  }

  @action
  handleOpenChat(): void {
    this.crisp.openChat();
  }

  @action
  handleDocumentClick(): void {
    if (this.settingsMenuOpen) {
      this.settingsMenuOpen = false;
    }

    if (this.moreMenuOpen) {
      this.moreMenuOpen = false;
    }
  }

  // Drive --sidebar-width via a body class so every layout consumer that reads
  // the CSS variable (main content, header, calendar, side panels) recomputes
  // when the sidebar is pinned open.
  #applyPinnedClass(): void {
    window.document.body.classList.toggle(PINNED_CLASS, this.pinned);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Ui::Sidebar': typeof UiSidebarComponent;
  }
}
